// Display · EmptyValue
//
// Canonical "no value" marker for KVGrid cells, table cells, CopyableValue
// fallbacks, and any field that needs an empty-state placeholder.
// Renders an em-dash (—) with muted color and regular weight so empty cells
// visually recede next to semibold filled values.
//
// Atom — no events, no configurable affordances. Output and Actions interfaces
// are dropped per the @ds atom convention (Rule 3 / ds-engineer.md).
//
// All visual values come from `@ds/tokens/palette`.

import type { ReactNode } from "react";
import { FONT_WEIGHT, TEXT } from "@ds/tokens/palette";

// ─── Inputs (data props — the model) ─────────────────────────────────────

export interface EmptyValueInput {
  /**
   * Override the default em-dash. Use when the domain expects a different
   * placeholder string (e.g. "N/A", "—", "brak").
   * Default: "—" (U+2014 EM DASH).
   */
  children?: ReactNode;
  /** `data-testid` applied to the root <span>. */
  testId?: string;
}

// ─── Component contract ──────────────────────────────────────────────────
//
// Atom: no Output, no Actions.

export type EmptyValueProps = EmptyValueInput;

// ─── Implementation ──────────────────────────────────────────────────────

export function EmptyValue({ children = "—", testId }: EmptyValueProps) {
  return (
    <span
      data-testid={testId}
      style={{
        fontWeight: FONT_WEIGHT.regular,
        color:      TEXT.faint,
      }}
    >
      {children}
    </span>
  );
}
